import * as Sentry from '@sentry/react';
import { SalesOrderSource } from 'src/types/application/SalesOrderDraftDTO';

/**
 * Source flags for determining the type of sales order source
 */
export interface SourceFlags {
  isEdiSource: boolean;
  isEmailSource: boolean;
  isDirectEntry: boolean;
  isFileUpload: boolean;
}

/**
 * Exhaustively determine source type flags - will cause compilation error if new source added
 */
export function getSourceFlags(
  source: SalesOrderSource | undefined,
): SourceFlags {
  if (!source) {
    return {
      isEdiSource: false,
      isEmailSource: false,
      isDirectEntry: false,
      isFileUpload: false,
    };
  }

  switch (source) {
    case SalesOrderSource.Edi:
      return {
        isEdiSource: true,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: false,
      };
    case SalesOrderSource.Email:
      return {
        isEdiSource: false,
        isEmailSource: true,
        isDirectEntry: false,
        isFileUpload: false,
      };
    case SalesOrderSource.DirectEntry:
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: true,
        isFileUpload: false,
      };
    case SalesOrderSource.FileUpload:
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: true,
      };
    default: {
      // Exhaustiveness check - will cause TS error if a new source is added
      const exhaustiveCheck: never = source;
      const error = new Error(`Unhandled SalesOrderSource: ${exhaustiveCheck}`);
      console.error(error);
      Sentry.captureException(error);
      return {
        isEdiSource: false,
        isEmailSource: false,
        isDirectEntry: false,
        isFileUpload: false,
      };
    }
  }
}

/**
 * Extract email domain from an email address string
 */
export function extractEmailDomain(input?: string): string | undefined {
  if (!input) return undefined;
  const match = input.toLowerCase().match(/@([^>\s]+)/);
  return match?.[1];
}

/**
 * Format a Date to YYYY-MM-DD string
 */
export function formatDateOnly(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string to Date (at midnight local time)
 */
export function parseDateOnlyString(s: string): Date {
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
  const date = new Date(y, (m || 1) - 1, d || 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Parse a Date, string, or null/undefined to Date or undefined
 */
export function parseDateOnly(value?: Date | string | null): Date | undefined {
  if (!value) return undefined;
  const dateString = typeof value === 'string' ? value : formatDateOnly(value);
  return parseDateOnlyString(dateString);
}

/**
 * Check if a date is in the past (only relevant when editable)
 */
export function isDatePast(date: Date | string, isEditable: boolean): boolean {
  const dateOnly = typeof date === 'string' ? date : formatDateOnly(date);
  const compare = parseDateOnlyString(dateOnly);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isEditable ? compare < today : false;
}
